import { useState, useEffect } from 'react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'
import { getStripePromise } from '@/lib/stripe'
import { api } from '@/lib/api'
import { useBrand } from '@/hooks/useBrand'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

export interface PaymentElementSuccess {
  setupIntentId: string
  paymentMethodId: string
}

export interface StripePaymentElementProps {
  tier: 'associate' | 'partner' | 'principal'
  interval: 'monthly' | 'annual'
  onSuccess: (result: PaymentElementSuccess) => void
  onError?: (message: string) => void
  submitLabel?: string
}

/**
 * Outer wrapper: initializes a SetupIntent via backend, then renders the
 * Elements provider with the returned client_secret.
 *
 * This is split into two components because <Elements> needs to wrap the
 * children that use Stripe hooks, and we can't use the hooks until we have
 * the client_secret from the backend.
 */
export function StripePaymentElement(props: StripePaymentElementProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    let cancelled = false
    setInitializing(true)

    api
      .post('/api/billing/checkout/init', { tier: props.tier, interval: props.interval })
      .then((res) => {
        if (cancelled) return
        setClientSecret(res.data.data.clientSecret)
        setSetupIntentId(res.data.data.setupIntentId)
      })
      .catch((err) => {
        if (cancelled) return
        const msg = err.response?.data?.message || err.message || 'Failed to start checkout'
        setError(msg)
        props.onError?.(msg)
      })
      .finally(() => {
        if (!cancelled) setInitializing(false)
      })

    return () => {
      cancelled = true
    }
  }, [props.tier, props.interval])

  if (initializing) {
    return (
      <div className="flex items-center justify-center py-12 text-white/60">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Preparing secure checkout…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
        <p className="text-sm text-red-300">{error}</p>
      </div>
    )
  }

  if (!clientSecret || !setupIntentId) {
    return <div className="text-white/60 text-sm">Checkout unavailable.</div>
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#4d71fd',
        colorBackground: '#0f1120',
        colorText: '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        borderRadius: '10px',
      },
    },
  }

  return (
    <Elements stripe={getStripePromise()} options={options}>
      <PaymentForm
        setupIntentId={setupIntentId}
        onSuccess={props.onSuccess}
        onError={props.onError}
        submitLabel={props.submitLabel}
      />
    </Elements>
  )
}

// ── Inner form — uses Stripe hooks ────────────────────────────────────────────

function PaymentForm(props: {
  setupIntentId: string
  onSuccess: (result: PaymentElementSuccess) => void
  onError?: (message: string) => void
  submitLabel?: string
}) {
  const stripe = useStripe()
  const elements = useElements()
  const brand = useBrand()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setSubmitting(true)
    setError(null)

    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/onboarding/step/4', // fallback if 3DS redirect
      },
      redirect: 'if_required', // only redirect when authentication is required
    })

    if (stripeError) {
      const msg = stripeError.message || 'Payment setup failed'
      setError(msg)
      props.onError?.(msg)
      setSubmitting(false)
      return
    }

    if (!setupIntent?.payment_method) {
      const msg = 'No payment method attached'
      setError(msg)
      props.onError?.(msg)
      setSubmitting(false)
      return
    }

    const paymentMethodId =
      typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method.id

    props.onSuccess({ setupIntentId: setupIntent.id, paymentMethodId })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'link'],
          // Override the mandate text's merchant name for just this Element.
          // The Stripe account's account-level display name is shared across
          // multiple products (Breakthrough Coaching, Revenue Intelligence,
          // etc.) — setting this here keeps the payment form branded
          // correctly without touching account-wide settings.
          business: { name: brand.appName },
        }}
      />
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300">
          {error}
        </div>
      )}
      <Button
        type="submit"
        size="lg"
        disabled={!stripe || !elements || submitting}
        className="w-full h-12 rounded-xl text-[15px] bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Confirming…
          </>
        ) : (
          props.submitLabel ?? 'Start my 14-day coverage'
        )}
      </Button>
      <p className="text-[11px] text-white/40 text-center">
        Your card will NOT be charged during the 14-day trial. We collect it now so your AI stays on the job when trial ends.
      </p>
    </form>
  )
}
